import { IconType } from 'react-icons';
import { LuCode2, LuHeading, LuList, LuPilcrow } from 'react-icons/lu';

import { Button } from '@frontend/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@frontend/components/ui/tooltip';
import { capitalizeFirstLetter } from '@frontend/shared/util/webHelpers';
import { Editor, useEditorState } from '@tiptap/react';

export interface EditorButtonsProps {
  editor: Editor;
}

export function EditorButtons({ editor }: EditorButtonsProps) {
  // Use useEditorState to track editor state changes for proper re-rendering in v3
  const { isActive: editorIsActive } = useEditorState({
    editor,
    selector: (snapshot) => ({
      isActive: (type: string) => {
        if (type === 'paragraph') {
          return (
            snapshot.editor.isActive(type) &&
            !snapshot.editor.isActive('heading') &&
            !snapshot.editor.isActive('bulletList') &&
            !snapshot.editor.isActive('codeBlock')
          );
        }
        return snapshot.editor.isActive(type);
      },
    }),
  });

  const isActive = (type: string) => {
    return editorIsActive(type);
  };

  const makeOnClick = (type: string) => () => {
    if (type === 'paragraph') {
      return editor.chain().focus().setParagraph().run();
    }
    if (type === 'bulletList' && !isActive('bulletList')) {
      return editor.chain().focus().toggleBulletList().run();
    }
    if (type === 'codeBlock') {
      return editor.chain().focus().setCodeBlock().run();
    }
    if (type === 'heading') {
      return editor.chain().focus().setHeading({ level: 1 }).run();
    }
  };

  const makeAriaLabel = (type: string) => {
    // Turn bulletList to Bullet List
    return capitalizeFirstLetter(type.replace(/([A-Z])/g, ' $1'));
  };

  const renderButton = (type: string, icon: IconType) => {
    const Icon = icon;
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            onClick={makeOnClick(type)}
            aria-label={makeAriaLabel(type)}
            size="sm"
            variant={isActive(type) ? 'default' : 'ghost'}
          >
            <Icon className="h-5 w-5" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          <p>{makeAriaLabel(type)}</p>
        </TooltipContent>
      </Tooltip>
    );
  };

  return (
    <TooltipProvider>
      <div className="flex gap-2 js-hide-unfocused mr-auto">
        {renderButton('paragraph', LuPilcrow)}
        {renderButton('heading', LuHeading)}
        {renderButton('bulletList', LuList)}
        {renderButton('codeBlock', LuCode2)}
      </div>
    </TooltipProvider>
  );
}
